import * as React from 'react';

import { CalendarIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/outline';
import { Disclosure } from '@headlessui/react';
import RenderSimpleHtml from '@core/components/display/string/RenderSimpleHtml';
import { classNames } from '@core/utils/functions/class-names';
import Link from 'next/link';
import ImageWithFallback from '@core/components/display/images/ImageWithFallback';

import type { Speaker } from '@/types/WebinarCustomFields';

export type WebinarCategory = 'upcoming' | 'ondemand';
interface WebinarCardProps {
    className?: string;
    title: string;
    category: WebinarCategory;
    location: string;
    date: React.ReactElement | string;
    description: string;
    registerLink?: string | null;
    registerText?: string;
    learnMoreLink?: string;
    learnMoreText?: string;
    imageUrl?: string;
    sponsoredBy?: string;
    speakers?: Speaker[];
}

const SponsoredBy = (props: { sponsoredBy?: string }): JSX.Element => {
    const { sponsoredBy } = props;

    if (!sponsoredBy) return <></>;

    return (
        <div className="w-full flex px-10 py-5 justify-start items-center flex-grow-0 flex-shrink-0 relative gap-1 border-l border-r">
            <p className="flex-grow-0 flex-shrink-0 text-[10px] text-left text-[#6a6a6a]">
                Sponsored by
                <span className="text-black text-xs ml-1">{sponsoredBy}</span>
            </p>
        </div>
    );
};

const SpeakerInfo = (props: { speaker: Speaker }): JSX.Element => (
    // todo: link to speaker page <Link href={props.speaker.name ? `/speaker/${slugify(props.speaker.name)}` : ''}>
    <>
        <div
            key={props.speaker.name}
            className="cursor-pointer flex flex-row py-2 justify-start items-start flex-grow relative gap-3"
        >
            {props.speaker.image && (
                <ImageWithFallback
                    alt="speaker"
                    src={props.speaker.image.sizes?.medium || ''}
                    width={50}
                    height={50}
                    fallbackSrc={props.speaker.image.url || ''}
                    className="w-[50px] h-[50px] rounded-full flex-grow-0 flex-shrink-0 object-cover"
                />
            )}
            <div className="flex flex-col justify-start items-start self-stretch flex-grow relative">
                <p className="self-stretch flex-grow-0 flex-shrink-0 w-full text-base text-left text-[#110b29]">
                    {props.speaker.name}
                </p>
                <p className="self-stretch flex-grow-0 flex-shrink-0 w-full text-xs text-left text-[#6a6a6a]">
                    {props.speaker.profession}
                </p>
            </div>
        </div>
    </>
);

const WebinarCard = (props: WebinarCardProps) => {
    const { className, category, title, date, description, learnMoreLink, registerLink, sponsoredBy, speakers } = props;

    const header =
        category === 'upcoming' ? (
            <div className="flex justify-start items-center flex-grow-0 flex-shrink-0 relative gap-1">
                <CalendarIcon className="w-2 h-2" />
                <div className="flex justify-start items-center flex-grow-0 flex-shrink-0 relative gap-1">
                    <p className="flex-grow-0 flex-shrink-0 text-[10px] text-left uppercase text-dark">
                        <>{date}</>
                    </p>
                </div>
            </div>
        ) : (
            <div className="flex justify-start items-center flex-grow-0 flex-shrink-0 gap-2">
                <div className="flex justify-start items-center flex-grow-0 flex-shrink-0 relative gap-1">
                    <p className="text-xxs text-left uppercase text-primary">regulation</p>
                </div>
            </div>
        );

    const footer =
        category === 'upcoming' ? (
            <div className="w-full">
                <SponsoredBy sponsoredBy={sponsoredBy} />
                <div className="w-full flex flex-col justify-start items-start self-stretch flex-grow-0 flex-shrink-0 border-l border-r border-b border-[#b4b4b4]/[0.35]">
                    <div className="flex flex-col justify-start items-start self-stretch flex-grow-0 flex-shrink-0 gap-3 px-10 py-5 border-t border-r-0 border-b-0 border-l-0 border-[#b4b4b4]/[0.35]">
                        <Disclosure>
                            {({ open }) => (
                                <>
                                    <Disclosure.Button
                                        className="flex justify-start items-center flex-grow-0 flex-shrink-0 relative gap-3"
                                        type="button"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#collapseExample"
                                        aria-expanded="false"
                                        aria-controls="collapseExample"
                                    >
                                        <p className="flex-grow-0 flex-shrink-0 text-sm text-left uppercase text-[#5637cd]">
                                            speakers
                                        </p>
                                        {open ? (
                                            <ChevronUpIcon className="w-3 h-3 text-primary" />
                                        ) : (
                                            <ChevronDownIcon className="w-3 h-3 text-primary" />
                                        )}
                                    </Disclosure.Button>
                                    <Disclosure.Panel>
                                        {speakers &&
                                            speakers.map((speaker, index) => (
                                                <SpeakerInfo key={`speaker-${index}`} speaker={speaker} />
                                            ))}
                                    </Disclosure.Panel>
                                </>
                            )}
                        </Disclosure>
                    </div>
                </div>
                {learnMoreLink && (
                    <div className="flex flex-col justify-start items-start self-stretch flex-grow-0 flex-shrink-0 border-l border-r border-b border-[#b4b4b4]/[0.35]">
                        <div className="flex justify-start items-center self-stretch flex-grow-0 flex-shrink-0 gap-3 px-10 py-5">
                            <div className="flex justify-start items-center flex-grow-0 flex-shrink-0 relative gap-3">
                                <Link href={registerLink || learnMoreLink} passHref>
                                    <a className="flex-grow-0 flex-shrink-0 text-sm text-left uppercase text-[#5637cd]">
                                        register
                                    </a>
                                </Link>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        ) : (
            <>
                {learnMoreLink && (
                    <div className="w-full flex flex-col justify-start items-center">
                        <SponsoredBy sponsoredBy={sponsoredBy} />
                        <div className="w-full px-10 flex border-l border-r border-b border-[#b4b4b4]/[0.35]">
                            <Link href={learnMoreLink} passHref>
                                <a className=" mt-5 mb-10 relative gap-2 px-5 py-4 text-white bg-primary border-primary border hover:bg-white hover:text-primary">
                                    <p className="text-sm text-center uppercase">access on demand</p>
                                </a>
                            </Link>
                        </div>
                    </div>
                )}
            </>
        );

    return (
        <div className={classNames('w-full h-auto flex flex-col justify-start items-start border-gray-200', className)}>
            <div className="lg:max-h-[325px] lg:h-80 xl:h-full px-10 pt-10 flex flex-col justify-start items-start border-t border-l border-r overflow-x-clip overflow-y-auto">
                <div className="flex flex-col justify-start items-start">
                    {header}
                    <div className="flex py-2 justify-start relative gap-2.5">
                        <p className="flex-grow">
                            <Link href={learnMoreLink || ''} passHref>
                                <a className="text-2xl font-semibold text-dark hover:text-primary">{title}</a>
                            </Link>
                        </p>
                    </div>
                </div>
                <RenderSimpleHtml html={description} className="flex-grow text-sm text-left font-light text-dark" />
            </div>
            {footer}
        </div>
    );
};

export default WebinarCard;
